import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import Section from './_Section'
import { transformPaddingToTailwind } from '../../helpers/style'
import { generateDataHash } from '../../helpers/contentHash'
import { MarkupContext } from '../../providers/Markup'

/** YIQ contrast: dark text on light fills, white on saturated/dark fills. */
function numberColourForBackground(hex) {
  if (!hex || typeof hex !== 'string')
    return '#0B304F'
  const normalised = hex.trim().replace('#', '')
  if (normalised.length !== 6)
    return '#0B304F'
  const r = Number.parseInt(normalised.slice(0, 2), 16)
  const g = Number.parseInt(normalised.slice(2, 4), 16)
  const b = Number.parseInt(normalised.slice(4, 6), 16)
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b))
    return '#0B304F'
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 165 ? '#0B304F' : '#FFFFFF'
}

function normaliseHex(hex) {
  if (!hex || typeof hex !== 'string')
    return null
  const t = hex.trim()
  if (t.startsWith('#'))
    return t
  return `#${t}`
}

function StepsCards(props) {
  const { createMarkup } = useContext(MarkupContext)
  const componentPadding = transformPaddingToTailwind(props.componentPadding, false, props.breadcrumbs)

  return (
    <Section
      type="stepsCards"
      outerClass={componentPadding}
      sectionTitle={false}
      {...props}
    >
      <div className="container mx-auto px-4 pb-4">
        {props.title && (
          <div
            className="text-center font-sans font-light text-xl md:text-2xl text-sky-500 mb-10 md:mb-12 max-w-3xl mx-auto leading-snug"
            data-type="steps-cards-intro"
            data-rte="true"
            dangerouslySetInnerHTML={createMarkup(props.title)}
          />
        )}

        {props.stepsCards && props.stepsCards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lg:gap-12 justify-items-center">
            {props.stepsCards.map((card) => {
              const bg = normaliseHex(card.backgroundColour) ?? '#927AAB'
              const numberColour = numberColourForBackground(bg)
              let statisticValue = card.statisticValue
              if (statisticValue && statisticValue.endsWith('+')) {
                statisticValue = <>
                  {statisticValue.substr(0, statisticValue.length - 1)}
                  <sup className="top-[-0.42em]">+</sup>
                </>
              }

              return (
                <div
                  key={generateDataHash(card)}
                  className="flex flex-col items-center text-center max-w-xs w-full"
                >
                  <div
                    className="flex items-center justify-center w-[7.5rem] h-[7.5rem] md:w-[8.5rem] md:h-[8.5rem] lg:w-40 lg:h-40 shrink-0"
                    style={{
                      backgroundColor: bg,
                      borderRadius: '0 50% 50% 50%',
                    }}
                  >
                    {card.statisticValue && (
                      <span
                        className="text-5xl md:text-6xl lg:text-7xl font-bold font-apex-book leading-none tracking-tight"
                        style={{ color: numberColour }}
                      >
                        {statisticValue}
                      </span>
                    )}
                  </div>

                  {card.description && (
                    <div
                      className="mt-5 md:mt-6 text-base md:text-lg text-neutral-600 font-sans font-normal leading-snug px-1"
                      data-rte="true"
                      dangerouslySetInnerHTML={createMarkup(card.description)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Section>
  )
}

StepsCards.propTypes = {
  componentPadding: PropTypes.string,
  breadcrumbs: PropTypes.any,
  title: PropTypes.string,
  description: PropTypes.string,
  stepsCards: PropTypes.arrayOf(
    PropTypes.shape({
      statisticValue: PropTypes.string,
      description: PropTypes.string,
      backgroundColour: PropTypes.string,
    })
  ),
}

export default StepsCards
